import classNames from 'classnames';
import { uniqueId } from 'lodash';
import { isObservableArray } from 'mobx';
import { PropTypes as MobxPropTypes, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormGroup, Input, Label } from 'reactstrap';

import CustomFormField from '../../../models/CustomFormField';
import FormField from '../../../models/FormField';
import { modelOf } from '../../../prop-types';
import CheckboxFieldItem from '../../../types/BooleanFieldItem';
import Field from '../Field';
import FieldError from '../FieldError';

const createUniqueKey = (option, index) => {
  return uniqueId(`${option}--${index}`);
};

const CheckboxField = ({
  label,
  fieldName,
  field,
  required,
  className,
  // unused here, but no reason to pass to Field with rest
  options,
  formName,
  updateExternalValue,
  labelStyle,
  inline,
  onChange,
  ...rest
}) => {
  const id = uniqueId(`${formName}__${fieldName}--`);

  const handleChange = (event) => {
    const isChecked = event.target.checked;
    const value = String(event.target.value);

    if (hasMultipleOptions()) {
      if (isChecked) {
        field.setValue([...field.value, value]);
      } else {
        removeValue(value);
      }
    } else {
      if (isChecked) {
        field.setValue(value);
      } else {
        field.setValue(null);
      }
    }

    // Pass value for external components in user interaction.
    if (updateExternalValue) {
      updateExternalValue(field);
    }
  };

  const hasMultipleOptions = () => {
    return Array.isArray(options) || isObservableArray(options);
  };

  const removeValue = (value) => {
    const newValue = [...field.value];
    const valueIndex = newValue.findIndex((item) => item === value);
    if (valueIndex !== -1) {
      newValue.splice(valueIndex, 1);
    }
    field.setValue(newValue);
  };

  const isChecked = (value) => {
    if (!field.value) {
      return false;
    }
    if (Array.isArray(field.value) || isObservableArray(field.value)) {
      return field.value.indexOf(String(value)) !== -1;
    }
    return value === field.value;
  };

  const getOptionArray = () => {
    if (hasMultipleOptions()) {
      return options;
    } else {
      return [options];
    }
  }

    return (
      <FormGroup
        className={classNames('CheckboxField', className)}
        tag="fieldset"
        id={id}
      >
        {label && (
          <div className="CheckboxField__label">
            {label}
            {required && ' *'}
          </div>
        )}
        {getOptionArray().map((option, index) => {
          const optionId = `${id}-${index}`;

          return (
            <FormGroup
              className="CheckboxFieldSet__checkbox-field"
              check
              key={createUniqueKey(option.value, index)}
              inline={inline}
            >
              <Field
                component={Input}
                field={field}
                type="checkbox"
                name={fieldName}
                id={optionId}
                checked={isChecked(option.value)}
                value={option.value}
                onChange={onChange || handleChange}
                invalid={field.errors.length > 0}
                required={required}
                {...rest}
              />
              <Label for={optionId} style={labelStyle} check>
                {option.label}
              </Label>
              <FieldError field={field} />
            </FormGroup>
          );
        })}
      </FormGroup>
    );
}

CheckboxField.propTypes = {
  field: PropTypes.oneOfType([modelOf(FormField), modelOf(CustomFormField)]).isRequired,
  formName: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,

  // Options should be:
  // - An array(-like), if the field value is expected to be an array of
  //   primitives, even with a single option
  // - A bare checkbox field shape, if the field value is expected to be
  //   a primitive
  options: PropTypes.oneOfType([
    CheckboxFieldItem,
    PropTypes.arrayOf(CheckboxFieldItem),
    MobxPropTypes.observableArrayOf(CheckboxFieldItem),
  ]).isRequired,
  className: PropTypes.string,
  inline: PropTypes.bool,
  labelStyle: PropTypes.object,
  label: PropTypes.node,
  onChange: PropTypes.func,
  updateExternalValue: PropTypes.func,
};

export default observer(CheckboxField);
